import React, { useState, useEffect, useRef, useMemo } from "react";
import { collection, query, where, getDocs } from "firebase/firestore";
import { db } from "../services/firebase";
import Layout from "../component/Layout";
import { calculateOrderSummary } from "../utils/calculations";
const ORDER_OVERDUE_MS = 60000;

const tableHeaderStyle = {
  border: "2px solid #1f2937",
  padding: "8px 12px",
  textAlign: "left",
  fontSize: "13px",
  fontWeight: "bold",
  backgroundColor: "#f3f4f6",
  color: "#4b5563",
};

const tableCellStyle = {
  border: "2px solid #1f2937",
  padding: "8px 12px",
  fontSize: "14px",
};

const TABS = [
  { key: "all", label: "Order Received" },
  { key: "ready", label: "Ready" },
  { key: "order", label: "Order" },
  { key: "openmarket", label: "Open Market" },
];

const OrderReceived = () => {
  const [orders, setOrders] = useState([]);
  const [activeTab, setActiveTab] = useState("all");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [sortBy, setSortBy] = useState("time_asc");
  const audioContextRef = useRef(null);
  const beepIntervalRef = useRef(null);
  const hasAttemptedInit = useRef(false);
  // Stable primitive dependency. `operator` is an object that the context may
  // recreate on each render, and `operator?.OperatorId` is undefined until the
  // operator is hydrated - passing that straight into `where()` makes Firestore
  // throw, which the catch below swallows, leaving the page permanently empty.
  const operatorId = localStorage.getItem("operatorId");

  // Try to initialize audio immediately (may fail silently in some browsers)
  const initAudio = () => {
    if (audioContextRef.current || hasAttemptedInit.current) return;
    hasAttemptedInit.current = true;
    try {
      const ctx = new (window.AudioContext || window.webkitAudioContext)();
      audioContextRef.current = ctx;
    } catch (e) {
      console.warn("Audio init failed (will retry on click):", e);
    }
  };

  useEffect(() => {
    const handleClick = () => {
      initAudio();
      document.body.removeEventListener("click", handleClick);
      document.body.removeEventListener("touchstart", handleClick);
    };
    document.body.addEventListener("click", handleClick);
    document.body.addEventListener("touchstart", handleClick);
    return () => {
      document.body.removeEventListener("click", handleClick);
      document.body.removeEventListener("touchstart", handleClick);
    };
  }, []);

  useEffect(() => {
    initAudio();
  }, []);

  // const playBeep = () => {
  //   if (audioContextRef.current?.state === "suspended") {
  //     audioContextRef.current.resume().catch(console.warn);
  //   }
  //   if (!audioContextRef.current) return;
  //   try {
  //     const ctx = audioContextRef.current;
  //     const oscillator = ctx.createOscillator();
  //     const gainNode = ctx.createGain();
  //     oscillator.connect(gainNode);
  //     gainNode.connect(ctx.destination);
  //     oscillator.type = "sine";
  //     oscillator.frequency.value = 1200;
  //     gainNode.gain.value = 0.2;
  //     oscillator.start();
  //     setTimeout(() => oscillator.stop(), 300);
  //   } catch (e) {
  //     console.warn("Beep failed:", e);
  //   }
  // };

  // Fetch received orders (Requested / Accepted / Assorted / FinalCorrection)
  useEffect(() => {
    // Don't query until the operator is available — Firestore throws when a
    // where() value is undefined, which left this page silently empty.
    if (!operatorId) {
      setOrders([]);
      return undefined;
    }

    let cancelled = false;

    const fetchOrders = async () => {
      const statuses = ["Requested", "Accepted","Assortment", "Assorted", "FinalCorrection"];
      try {
        const q = query(
          collection(db, "orderList"),
          where("operatorId", "==", operatorId),
          where("orderStatus", "in", statuses),
        );
        const querySnapshot = await getDocs(q);
        const ordersData = [];
        querySnapshot.forEach((doc) => {
          const data = doc.data();
          let orderTime = null;
          if (data.orderTime?.seconds) {
            orderTime = new Date(data.orderTime.seconds * 1000);
          } else if (data.addedAt) {
            orderTime = new Date(data.addedAt);
          }
          ordersData.push({
            id: doc.id,
            ...data,
            orderTime,
            isOverdue: orderTime && new Date() - orderTime > ORDER_OVERDUE_MS,
          });
        });
        ordersData.sort((a, b) => (a.orderTime || 0) - (b.orderTime || 0));
        if (cancelled) return;
        setOrders(ordersData);
      } catch (error) {
        console.error("Fetch error:", error);
      }
    };
    fetchOrders();
    return () => {
      cancelled = true;
    };
  }, [operatorId]);

  // Continuous beep check for overdue orders
  useEffect(() => {
    const checkAndBeep = () => {
      const now = new Date();
      const hasOverdue = orders.some(
        (order) => order.orderTime && now - order.orderTime > ORDER_OVERDUE_MS,
      );
      if (hasOverdue) {
        // playBeep();
      }
    };
    checkAndBeep();
    beepIntervalRef.current = setInterval(checkAndBeep, 10000);
    return () => {
      const intervalId = beepIntervalRef.current;
      if (intervalId) clearInterval(intervalId);
    };
  }, [orders]);

  const formatDate = (date) => (date ? date.toLocaleString("en-IN") : "—");
  const getAge = (orderTime) => {
    if (!orderTime) return "—";
    const diffMs = new Date() - orderTime;
    const mins = Math.floor(diffMs / 60000);
    const secs = Math.floor((diffMs % 60000) / 1000);
    return `${mins}m ${secs}s`;
  };

  const getStatusStyle = (status) => ({
    display: "inline-block",
    padding: "2px 6px",
    fontSize: "11px",
    fontWeight: "bold",
    borderRadius: "4px",
    textTransform: "capitalize",
    backgroundColor:
      status === "Requested"
        ? "#fef3c7"
        : status === "Accepted"
          ? "#dcfce7"
          : status === "Assorted"
            ? "#ede9fe"
            : "#e5e7eb",
    color:
      status === "Requested"
        ? "#92400e"
        : status === "Accepted"
          ? "#166534"
          : status === "Assorted"
            ? "#5b21b6"
            : "#374151",
  });

  // Returns per-size breakdown rows for an order
  const getSizeRows = (order) => {
    const rows = [];
    const variants = order.selectedVariants || [];
    const lots = order.lotDetails || [];
    const lotBySize = {};
    lots.forEach((l) => {
      if (l.size != null) lotBySize[l.size] = l;
    });

    if (variants.length) {
      variants.forEach((v) => {
        const lot = lotBySize[v.size];
        rows.push({
          size: v.size,
          qty: v.quantity,
          netWt: lot?.netWt ?? order.netWt,
          grossWt: lot?.grossWt ?? order.grossWt,
          set: lot?.set ?? v.quantity,
        });
      });
    } else if (lots.length) {
      lots.forEach((l) => {
        rows.push({
          size: l.size,
          qty: l.set,
          netWt: l.netWt,
          grossWt: l.grossWt,
          set: l.set,
        });
      });
    } else if (order.orderDetail && typeof order.orderDetail === "object") {
      Object.entries(order.orderDetail).forEach(([size, d]) => {
        rows.push({
          size,
          qty: d.qty ?? d.set ?? 1,
          netWt: d.netWt ?? d.netwt,
          grossWt: d.grossWt ?? d.grossWT,
          set: d.set ?? d.qty ?? 1,
        });
      });
    }
    return rows;
  };

  // Apply search + status filter + sorting before grouping
  const processedOrders = useMemo(() => {
    let list = orders;

    if (activeTab !== "all") {
      list = list.filter((o) => o.serviceType === activeTab);
    }

    if (statusFilter !== "all") {
      list = list.filter((o) => (o.orderStatus || "Unknown") === statusFilter);
    }

    const q = search.trim().toLowerCase();
    if (q) {
      list = list.filter(
        (o) =>
          (o.buyerId || "").toLowerCase().includes(q) ||
          (o.sellerId || "").toLowerCase().includes(q) ||
          (o.id || "").toLowerCase().includes(q) ||
          (o.operatorId || "").toLowerCase().includes(q) ||
          (o.productName || "").toLowerCase().includes(q),
      );
    }

    const sorted = [...list];
    switch (sortBy) {
      case "time_asc":
        sorted.sort((a, b) => (a.orderTime || 0) - (b.orderTime || 0));
        break;
      case "time_desc":
        sorted.sort((a, b) => (b.orderTime || 0) - (a.orderTime || 0));
        break;
      case "age_asc":
        sorted.sort((a, b) => (a.orderTime || 0) - (b.orderTime || 0));
        break;
      case "age_desc":
        sorted.sort((a, b) => (b.orderTime || 0) - (a.orderTime || 0));
        break;
      case "net_asc":
        sorted.sort(
          (a, b) => (parseFloat(a.netWt) || 0) - (parseFloat(b.netWt) || 0),
        );
        break;
      case "net_desc":
        sorted.sort(
          (a, b) => (parseFloat(b.netWt) || 0) - (parseFloat(a.netWt) || 0),
        );
        break;
      case "gross_asc":
        sorted.sort(
          (a, b) => (parseFloat(a.grossWt) || 0) - (parseFloat(b.grossWt) || 0),
        );
        break;
      case "gross_desc":
        sorted.sort(
          (a, b) => (parseFloat(b.grossWt) || 0) - (parseFloat(a.grossWt) || 0),
        );
        break;
      default:
        break;
    }
    return sorted;
  }, [orders, activeTab, statusFilter, search, sortBy]);

  // Count of orders per tab (respects search + status filter, ignores active tab)
  const counts = useMemo(() => {
    let base = orders;
    const q = search.trim().toLowerCase();
    if (q) {
      base = base.filter(
        (o) =>
          (o.buyerId || "").toLowerCase().includes(q) ||
          (o.sellerId || "").toLowerCase().includes(q) ||
          (o.id || "").toLowerCase().includes(q) ||
          (o.operatorId || "").toLowerCase().includes(q) ||
          (o.productName || "").toLowerCase().includes(q),
      );
    }
    if (statusFilter !== "all") {
      base = base.filter((o) => (o.orderStatus || "Unknown") === statusFilter);
    }
    const c = { all: base.length, ready: 0, order: 0, openmarket: 0 };
    base.forEach((o) => {
      if (o.serviceType === "ready") c.ready += 1;
      else if (o.serviceType === "order") c.order += 1;
      else if (o.serviceType === "openmarket") c.openmarket += 1;
    });
    return c;
  }, [orders, search, statusFilter]);

  // Group filtered orders: buyer -> seller -> status, with totals at every level
  const groupedData = useMemo(() => {
    const filtered = processedOrders;

    const emptyTotals = () => ({
      totalOrders: 0,
      totalGrossWt: 0,
      totalNetWt: 0,
      totalSets: 0,
      totalAmount: 0,
    });

    const buyers = {};

    filtered.forEach((order) => {
      const buyerId = order.buyerId || "Unknown Buyer";
      const sellerId = order.sellerId || "Unknown Seller";
      const status = order.orderStatus || "Unknown";

      if (!buyers[buyerId])
        buyers[buyerId] = { sellers: {}, totals: emptyTotals() };
      if (!buyers[buyerId].sellers[sellerId])
        buyers[buyerId].sellers[sellerId] = {
          statuses: {},
          totals: emptyTotals(),
        };
      if (!buyers[buyerId].sellers[sellerId].statuses[status]) {
        buyers[buyerId].sellers[sellerId].statuses[status] = {
          orders: [],
          totals: emptyTotals(),
        };
      }

      const sGroup = buyers[buyerId].sellers[sellerId].statuses[status];
      sGroup.orders.push(order);

      const summary = calculateOrderSummary(order);
      const gross = summary.gross;
      const net = summary.net;
      const sets = summary.sets;
      const amount = summary.amount;

      sGroup.totals.totalOrders += 1;
      sGroup.totals.totalGrossWt += gross;
      sGroup.totals.totalNetWt += net;
      sGroup.totals.totalSets += sets;
      sGroup.totals.totalAmount += amount;

      const seller = buyers[buyerId].sellers[sellerId];
      seller.totals.totalOrders += 1;
      seller.totals.totalGrossWt += gross;
      seller.totals.totalNetWt += net;
      seller.totals.totalSets += sets;
      seller.totals.totalAmount += amount;

      const buyer = buyers[buyerId];
      buyer.totals.totalOrders += 1;
      buyer.totals.totalGrossWt += gross;
      buyer.totals.totalNetWt += net;
      buyer.totals.totalSets += sets;
      buyer.totals.totalAmount += amount;
    });

    // Format numeric totals
    const fmt = (t) => ({
      totalOrders: t.totalOrders,
      totalGrossWt: t.totalGrossWt.toFixed(3),
      totalNetWt: t.totalNetWt.toFixed(3),
      totalSets: t.totalSets.toFixed(3),
      totalAmount: t.totalAmount.toFixed(2),
    });

    Object.values(buyers).forEach((buyer) => {
      Object.values(buyer.sellers).forEach((seller) => {
        Object.values(seller.statuses).forEach((status) => {
          status.totals = fmt(status.totals);
        });
        seller.totals = fmt(seller.totals);
      });
      buyer.totals = fmt(buyer.totals);
    });

    return buyers;
  }, [processedOrders]);

  const buyerEntries = Object.entries(groupedData);

  return (
    <Layout pageTitle="Order Received">
      <div style={{ padding: "16px", overflowX: "auto" }}>
        {/* Tabs */}
        <div style={{ display: "flex", gap: "8px", marginBottom: "16px" }}>
          {TABS.map((tab) => (
            <button
              key={tab.key}
              onClick={() => setActiveTab(tab.key)}
              style={{
                padding: "8px 18px",
                border: "2px solid #1f2937",
                borderRadius: "8px",
                fontSize: "14px",
                fontWeight: "bold",
                cursor: "pointer",
                backgroundColor: activeTab === tab.key ? "#1f2937" : "#ffffff",
                color: activeTab === tab.key ? "#ffffff" : "#1f2937",
              }}
            >
              {tab.label} ({counts[tab.key]})
            </button>
          ))}
        </div>

        {/* Search / Filter / Sort controls */}
        <div
          style={{
            display: "flex",
            gap: "12px",
            marginBottom: "16px",
            alignItems: "center",
          }}
        >
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search Buyer / Seller / Order ID / Operator / Product"
            style={{
              flex: "1 1 280px",
              minWidth: "60%",
              padding: "9px 12px",
              border: "2px solid #1f2937",
              borderRadius: "8px",
              fontSize: "14px",
              boxSizing: "border-box",
            }}
          />
          <span
            style={{
              fontSize: "13px",
              fontWeight: "bold",
              color: "#4b5563",
              whiteSpace: "nowrap",
            }}
          >
            Filter:
          </span>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            style={{
              padding: "9px 12px",
              border: "2px solid #1f2937",
              borderRadius: "8px",
              fontSize: "14px",
            }}
          >
            <option value="all">All Status</option>
            <option value="Requested">Requested</option>
            <option value="Accepted">Accepted</option>
            <option value="Assorted">Assorted</option>
            <option value="FinalCorrection">FinalCorrection</option>
          </select>
          <span
            style={{
              fontSize: "13px",
              fontWeight: "bold",
              color: "#4b5563",
              whiteSpace: "nowrap",
            }}
          >
            Sort:
          </span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            style={{
              padding: "9px 12px",
              border: "2px solid #1f2937",
              borderRadius: "8px",
              fontSize: "14px",
            }}
          >
            <option value="time_asc">Order Time (Oldest)</option>
            <option value="time_desc">Order Time (Newest)</option>
            <option value="age_asc">Age (Oldest)</option>
            <option value="age_desc">Age (Newest)</option>
            <option value="net_asc">Net Wt (Low → High)</option>
            <option value="net_desc">Net Wt (High → Low)</option>
            <option value="gross_asc">Gross Wt (Low → High)</option>
            <option value="gross_desc">Gross Wt (High → Low)</option>
          </select>
        </div>

        {buyerEntries.length === 0 ? (
          <p
            style={{
              textAlign: "center",
              color: "#6b7280",
              paddingTop: "32px",
              fontSize: "18px",
            }}
          >
            No orders received.
          </p>
        ) : (
          buyerEntries.map(([buyerId, buyer]) => (
            <div
              key={buyerId}
              style={{
                marginBottom: "28px",
                border: "2px solid #1f2937",
                borderRadius: "8px",
                overflow: "hidden",
              }}
            >
              {/* Buyer summary row */}
              <div
                style={{
                  backgroundColor: "#1f2937",
                  color: "#ffffff",
                  padding: "10px 14px",
                  display: "flex",
                  flexWrap: "wrap",
                  gap: "16px",
                  alignItems: "center",
                }}
              >
                <strong style={{ fontSize: "16px" }}>Buyer: {buyerId}</strong>
                <span>Orders: {buyer.totals.totalOrders}</span>
                <span>Gross: {buyer.totals.totalGrossWt}g</span>
                <span>Net: {buyer.totals.totalNetWt}g</span>
                <span>Sets: {buyer.totals.totalSets}</span>
                <span>Amount: ₹{buyer.totals.totalAmount}</span>
              </div>

              {Object.entries(buyer.sellers).map(([sellerId, seller]) => (
                <div key={sellerId} style={{ borderTop: "2px solid #1f2937" }}>
                  {Object.entries(seller.statuses).map(
                    ([status, statusGroup]) => (
                      <div
                        key={status}
                        style={{ borderTop: "1px solid #d1d5db" }}
                      >
                        {/* Status summary row */}
                        <div
                          style={{
                            backgroundColor: "#f3f4f6",
                            padding: "6px 14px",
                            display: "flex",
                            flexWrap: "wrap",
                            gap: "16px",
                            alignItems: "center",
                          }}
                        >
                          <span style={getStatusStyle(status)}>{status}</span>
                          <span>Orders: {statusGroup.totals.totalOrders}</span>
                          <span>Gross: {statusGroup.totals.totalGrossWt}g</span>
                          <span>Net: {statusGroup.totals.totalNetWt}g</span>
                          <span>Sets: {statusGroup.totals.totalSets}</span>
                          <span>Amount: ₹{statusGroup.totals.totalAmount}</span>
                        </div>

                        {/* Orders table with size-wise detailing */}
                        <table
                          style={{
                            minWidth: "100%",
                            borderCollapse: "collapse",
                            borderBottom: "2px solid #1f2937",
                          }}
                        >
                          <thead>
                            <tr style={{ backgroundColor: "#ffffff" }}>
                              <th style={tableHeaderStyle}>Image</th>
                              <th style={tableHeaderStyle}>Order ID</th>
                              <th style={tableHeaderStyle}>Product</th>
                              <th style={tableHeaderStyle}>Size</th>
                              <th style={tableHeaderStyle}>Qty</th>
                              <th style={tableHeaderStyle}>Net Wt (g)</th>
                              <th style={tableHeaderStyle}>Gross Wt (g)</th>
                              <th style={tableHeaderStyle}>Operator ID</th>
                              <th style={tableHeaderStyle}>Order Time</th>
                              <th style={tableHeaderStyle}>Age</th>
                            </tr>
                          </thead>
                          <tbody>
                            {statusGroup.orders.map((order) => {
                              const sizeRows = getSizeRows(order);
                              const isOverdue = order.isOverdue;
                              const baseBg = isOverdue ? "#fee2e2" : "#ffffff";
                              return (
                                <React.Fragment key={order.id}>
                                  <tr
                                    style={{
                                      backgroundColor: baseBg,
                                      borderBottom: "1px solid #e5e7eb",
                                    }}
                                  >
                                    <td
                                      style={{
                                        ...tableCellStyle,
                                        border: "none",
                                        textAlign: "center",
                                      }}
                                    >
                                      {order.images?.[0]?.url ? (
                                        <img
                                          src={order.images[0].url.trim()}
                                          alt="Product"
                                          style={{
                                            height: "40px",
                                            width: "40px",
                                            objectFit: "contain",
                                            border: "1px solid #d1d5db",
                                            borderRadius: "4px",
                                          }}
                                        />
                                      ) : (
                                        <span style={{ color: "#9ca3af" }}>
                                          —
                                        </span>
                                      )}
                                    </td>
                                    <td
                                      style={{
                                        ...tableCellStyle,
                                        border: "none",
                                        fontFamily: "monospace",
                                        fontSize: "12px",
                                      }}
                                    >
                                      {order.id}
                                    </td>
                                    <td
                                      style={{
                                        ...tableCellStyle,
                                        border: "none",
                                        fontWeight: "bold",
                                      }}
                                    >
                                      {order.productName || "—"}
                                    </td>
                                    <td
                                      style={{
                                        ...tableCellStyle,
                                        border: "none",
                                      }}
                                    >
                                      {sizeRows.length
                                        ? sizeRows.map((r, i) => (
                                            <div key={i}>{r.size}</div>
                                          ))
                                        : "—"}
                                    </td>
                                    <td
                                      style={{
                                        ...tableCellStyle,
                                        border: "none",
                                      }}
                                    >
                                      {sizeRows.length
                                        ? sizeRows.reduce(
                                            (s, r) =>
                                              s + (parseFloat(r.qty) || 0),
                                            0,
                                          )
                                        : "—"}
                                    </td>
                                    <td
                                      style={{
                                        ...tableCellStyle,
                                        border: "none",
                                      }}
                                    >
                                      {order.netWt || "—"}
                                    </td>
                                    <td
                                      style={{
                                        ...tableCellStyle,
                                        border: "none",
                                      }}
                                    >
                                      {order.grossWt || "—"}
                                    </td>
                                    <td
                                      style={{
                                        ...tableCellStyle,
                                        border: "none",
                                        fontFamily: "monospace",
                                        fontSize: "12px",
                                      }}
                                    >
                                      {order.operatorId || "—"}
                                    </td>
                                    <td
                                      style={{
                                        ...tableCellStyle,
                                        border: "none",
                                      }}
                                    >
                                      {formatDate(order.orderTime)}
                                    </td>
                                    <td
                                      style={{
                                        ...tableCellStyle,
                                        border: "none",
                                        fontFamily: "monospace",
                                      }}
                                    >
                                      {getAge(order.orderTime)}
                                    </td>
                                  </tr>
                                  {/* Size-wise detailing */}
                                  {sizeRows.map((r, i) => (
                                    <tr
                                      key={`${order.id}-${i}`}
                                      style={{
                                        backgroundColor: "#fafafa",
                                        borderBottom: "1px solid #eef2f7",
                                      }}
                                    >
                                      <td
                                        style={{
                                          ...tableCellStyle,
                                          border: "none",
                                        }}
                                      ></td>
                                      <td
                                        style={{
                                          ...tableCellStyle,
                                          border: "none",
                                        }}
                                      ></td>
                                      <td
                                        style={{
                                          ...tableCellStyle,
                                          border: "none",
                                          color: "#6b7280",
                                          paddingLeft: "24px",
                                        }}
                                        colSpan={2}
                                      >
                                        ↳ Size: <strong>{r.size}</strong>
                                      </td>
                                      <td
                                        style={{
                                          ...tableCellStyle,
                                          border: "none",
                                          color: "#6b7280",
                                        }}
                                      >
                                        {r.qty}
                                      </td>
                                      <td
                                        style={{
                                          ...tableCellStyle,
                                          border: "none",
                                          color: "#6b7280",
                                        }}
                                      >
                                        {r.netWt ?? "—"}
                                      </td>
                                      <td
                                        style={{
                                          ...tableCellStyle,
                                          border: "none",
                                          color: "#6b7280",
                                        }}
                                      >
                                        {r.grossWt ?? "—"}
                                      </td>
                                      <td
                                        style={{
                                          ...tableCellStyle,
                                          border: "none",
                                        }}
                                      ></td>
                                      <td
                                        style={{
                                          ...tableCellStyle,
                                          border: "none",
                                        }}
                                        colSpan={2}
                                      ></td>
                                    </tr>
                                  ))}
                                </React.Fragment>
                              );
                            })}
                            {/* Status-wise total */}
                            <tr style={{ backgroundColor: "#dbeafe" }}>
                              <td
                                style={{
                                  ...tableCellStyle,
                                  border: "none",
                                  fontWeight: "bold",
                                }}
                                colSpan={4}
                              >
                                Total ({status})
                              </td>
                              <td
                                style={{
                                  ...tableCellStyle,
                                  border: "none",
                                  fontWeight: "bold",
                                }}
                              >
                                Sets: {statusGroup.totals.totalSets}
                              </td>
                              <td
                                style={{
                                  ...tableCellStyle,
                                  border: "none",
                                  fontWeight: "bold",
                                }}
                              >
                                Net: {statusGroup.totals.totalNetWt}g
                              </td>
                              <td
                                style={{
                                  ...tableCellStyle,
                                  border: "none",
                                  fontWeight: "bold",
                                }}
                              >
                                Gross: {statusGroup.totals.totalGrossWt}g
                              </td>
                              <td
                                style={{
                                  ...tableCellStyle,
                                  border: "none",
                                  fontWeight: "bold",
                                }}
                                colSpan={3}
                              >
                                ₹{statusGroup.totals.totalAmount}
                              </td>
                            </tr>
                          </tbody>
                        </table>
                      </div>
                    ),
                  )}
                </div>
              ))}
            </div>
          ))
        )}
      </div>
    </Layout>
  );
};

export default OrderReceived;
